import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AiChat, AiChatEvent } from '@qtek/shared/models';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { AiChatActions } from './ai-chat.actions';
import { AiChatService } from './ai-chat.service';
import { normalizeChatEvent } from './normalize-chat-event';

@Injectable()
export class AiChatEffects {
  private actions$ = inject(Actions);
  private chatSvc = inject(AiChatService);

  private normalizeChat(raw: any): AiChat | null {
    if (!raw) return null;
    const id = raw?.id ?? raw?._id;
    if (!id) return null;
    const title = raw?.title ?? raw?.name ?? raw?.subject;
    const preview =
      typeof raw?.preview === 'string'
        ? raw.preview
        : typeof raw?.summary === 'string'
          ? raw.summary
          : typeof raw?.snippet === 'string'
            ? raw.snippet
            : undefined;
    const updatedAtSource =
      raw?.updatedAt ?? raw?._uts ?? raw?.uts ?? raw?.updated;
    let updatedAt: number | undefined;
    if (typeof updatedAtSource === 'number') {
      updatedAt = updatedAtSource;
    } else if (typeof updatedAtSource === 'string') {
      const parsed = Date.parse(updatedAtSource);
      if (!Number.isNaN(parsed)) {
        updatedAt = parsed;
      }
    }
    // SD-2661: these must always be present as explicit keys, never omitted. The BE serializes
    // ChatApi with NON_NULL, so an unpinned chat arrives with no `pinnedAt` field at all — and the
    // reducer merges with `{ ...existing, ...incoming }`, so a missing key would preserve the old
    // pin and unpinning would appear to do nothing.
    const pinnedAt = this.toEpochMillis(raw?.pinnedAt);
    const deletedAt = this.toEpochMillis(raw?.deletedAt);
    return { id, title, preview, updatedAt, pinnedAt, deletedAt };
  }

  /** ISO instant or epoch millis -> epoch millis; anything absent/unparseable -> null. */
  private toEpochMillis(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return null;
  }

  subscribeChats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiChatActions.subscribeChats),
      switchMap(({ payload }) =>
        this.chatSvc.subscribeChats(payload).pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(AiChatActions.unsubscribeChats),
              filter(({ mysid }) => mysid === payload.mysid)
            )
          ),
          map((msg: any) => {
            const { op, res, id, sts, mysid } = msg;
            switch (op) {
              case 'query': {
                if (!res) {
                  return AiChatActions.getChatsFailure({ error: sts });
                }
                const chats = (res as any[])
                  .map(item => this.normalizeChat(item))
                  .filter((item): item is AiChat => !!item);
                return AiChatActions.getChatsSuccess({ chats, mysid });
              }
              case 'ins': {
                const normalized =
                  this.normalizeChat(res) ??
                  ({
                    id: (id as string) ?? (res?.id as string),
                    title: res?.title,
                  } as AiChat);
                if (!normalized.updatedAt) {
                  normalized.updatedAt = Date.now();
                }
                return AiChatActions.createChatSuccess({
                  chat: normalized,
                  mysid,
                });
              }
              case 'upd': {
                const normalized =
                  this.normalizeChat(res) ??
                  ({
                    id: (res as any)?.id,
                    title: (res as any)?.title,
                  } as AiChat);
                // SD-2661: the BE soft-deletes, so a delete reaches other sessions as an `upd`
                // carrying `deletedAt`. Treat it as a removal, not an update.
                if (normalized.deletedAt) {
                  return AiChatActions.deleteChatSuccess({ id: normalized.id });
                }
                if (!normalized.updatedAt) {
                  normalized.updatedAt = Date.now();
                }
                return AiChatActions.updateChatSuccess({ chat: normalized });
              }
              // Kept for completeness: the BE never hard-deletes a Chat today, but a `del`
              // broadcast must still drop the row rather than fall through to the no-op default.
              case 'del': {
                const deletedId = (id as string) ?? (res as any)?.id;
                return deletedId
                  ? AiChatActions.deleteChatSuccess({ id: deletedId })
                  : { type: '[AI Chat] Unknown chats WS op' };
              }
              default:
                return { type: '[AI Chat] Unknown chats WS op' };
            }
          }),
          catchError(error => of(AiChatActions.getChatsFailure({ error })))
        )
      )
    )
  );

  getChats$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AiChatActions.getChats),
        tap(({ payload }) => this.chatSvc.queryChats(payload))
      ),
    { dispatch: false }
  );

  createChat$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AiChatActions.createChat),
        tap(({ payload }) => {
          this.chatSvc.createChat(payload);
        })
      ),
    { dispatch: false }
  );

  updateChat$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AiChatActions.updateChat),
        tap(({ payload }) => this.chatSvc.updateChat(payload))
      ),
    { dispatch: false }
  );

  deleteChat$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AiChatActions.deleteChat),
        tap(({ payload }) => this.chatSvc.deleteChat(payload))
      ),
    { dispatch: false }
  );

  subscribeChatEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiChatActions.subscribeChatEvents),
      switchMap(({ payload }) =>
        this.chatSvc.subscribeChatEvents(payload).pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(AiChatActions.unsubscribeChatEvents),
              filter(({ mysid }) => mysid === payload.mysid)
            )
          ),
          map((msg: any) => {
            const { op, res, sts, mysid } = msg;
            switch (op) {
              case 'query':
                return res
                  ? AiChatActions.getChatEventsSuccess({
                      events: (res as any[])
                        .map(e => normalizeChatEvent(e))
                        .filter((e): e is AiChatEvent => !!e),
                      mysid,
                    })
                  : AiChatActions.getChatEventsFailure({ error: sts });
              case 'ins': {
                const event = normalizeChatEvent(res);
                return event
                  ? AiChatActions.getChatEventsSuccess({
                      events: [event],
                      mysid,
                    })
                  : { type: '[AI Chat] Ignored empty chat event' };
              }
              default:
                return { type: '[AI Chat] Unknown events WS op' };
            }
          }),
          catchError(error => of(AiChatActions.getChatEventsFailure({ error })))
        )
      )
    )
  );

  getChatEvents$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AiChatActions.getChatEvents),
        tap(({ payload }) => this.chatSvc.queryChatEvents(payload))
      ),
    { dispatch: false }
  );

  // SD-2526 Stage B: outbound resync request, routed through the store like every other WS op.
  resyncStream$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AiChatActions.resyncStream),
        tap(({ payload }) => this.chatSvc.resyncStream(payload))
      ),
    { dispatch: false }
  );

  sendMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AiChatActions.sendMessage),
        tap(({ payload }) => {
          this.chatSvc.sendEvent(payload);
        })
      ),
    { dispatch: false }
  );
}
